import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { CompareResult } from 'src/app/compare-result';
import { PlatePaymentDetails } from 'src/app/models/platePaymentDetails';
import { RegistrationService } from 'src/app/services/registration-service';
import { CompareService } from 'src/app/services/compare-service';
import { TrackingService } from 'src/app/services/tracking-service';
import { CommerceService } from 'src/app/services/commerce-service';
import { SessionService } from 'src/app/services/session-service';
import { Subscription, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';

declare global {
  interface Window {
    gtag: any;
  }
}

export type MarketplaceKind = 'new-issue' | 'resale';
export type LiveState = 'checking' | 'live' | 'last-seen';
type SortKey = 'price-asc' | 'price-desc' | 'trust';

// Dealer marketplace section for plate-detail-v2. This re-skins the EXISTING
// dealer comparison to the "PlateX 33.3" prototype — it does NOT introduce a new
// table or fabricate data. It reuses the same payment-details data source, the
// same total calculation (CompareService.calculateFoundRows), the same
// Trustpilot CDN star rendering, and the same affiliate-click behaviour
// (gtag + TrackingService + CommerceService.recordClick + tokenised window.open)
// that pla-tp-sellers uses on the production page.
@Component({
  selector: 'pla-pdv2-marketplace',
  templateUrl: './marketplace.component.html',
  styleUrls: ['./marketplace.component.scss'],
})
export class MarketplaceComponent implements OnChanges, OnDestroy {
  @Input() public registration: string = '';
  // Drives the conditional supporting copy only; defaults to the resale wording.
  @Input() public kind: MarketplaceKind = 'resale';
  // DVLA's own offer for a new-release plate not yet in the DB (27 pre-sale).
  // Shown as the first row; it is not a dealer, so it stays out of the range.
  @Input() public dvla: { price: number; href: string; note: string; cta: string } | null = null;

  // Emits the real lowest/highest live listing totals (or null when there are
  // none) so the hero can show a "Range £LOW – £HIGH" summary from the SAME
  // data this table renders — no extra backend call in the hero.
  @Output() public priceRange = new EventEmitter<{
    low: number;
    high: number;
    sellers: number;
  } | null>();

  public loading: boolean = true;

  // Staged loading: stored prices render immediately; dealers that need a paid
  // unblocker and whose stored price is stale (needsLiveCheck from the API) get a
  // per-row loader while their live price is fetched individually. Keyed by site.
  public liveState: { [site: string]: LiveState } = {};
  private lastSeenAt: { [site: string]: string } = {};
  private liveSubs: Subscription[] = [];
  private liveGeneration = 0;
  public rows: CompareResult[] = [];
  public sort: SortKey = 'price-asc';
  public expanded: boolean = false;
  private readonly pageSize: number = 9;

  // Filters — same predicates the existing tp-sellers comparison uses, applied
  // in-memory to the already-loaded rows. No new backend calls are introduced.
  public filtersOpen: boolean = false;
  public filters = {
    standardPlates: false,
    managedTransfer: false,
    offersKlarna: false,
    offersBespokeFinance: false,
    rating: 'any' as 'any' | '3' | '4' | '5',
  };

  // Explanatory accordion content (generic; does not reflect any single seller).
  public readonly includedServices: string[] = [
    'Managed transfer',
    'Registration plates supplied',
    'Transfer timing assistance',
    'Finance options',
    'Telephone support',
    'Aftercare and problem resolution',
  ];

  constructor(
    private registrationService: RegistrationService,
    private compareService: CompareService,
    private trackingService: TrackingService,
    private commerceService: CommerceService,
    private sessionService: SessionService
  ) {}

  ngOnDestroy(): void {
    this.cancelLiveChecks();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['registration'] && this.registration) {
      this.load();
    }
  }

  private load(): void {
    this.cancelLiveChecks();
    this.loading = true;
    this.expanded = false;
    this.rows = [];
    this.registrationService
      .getPlatePaymentDetails(this.registration.replace(' ', ''))
      .subscribe(
        (details: PlatePaymentDetails) => {
          const sellers =
            details && details.sellersResp ? details.sellersResp.sellers : [];
          // Same total calculation the live page uses (certificate price only;
          // transfer/plates excluded from the headline total).
          const calculated = this.compareService.calculateFoundRows(
            sellers || [],
            false,
            false
          );
          this.rows = (calculated || []).filter(
            (r) => r.calculated && r.total > 0
          );
          this.applySort();
          this.emitRange();
          this.loading = false;
          this.startLiveChecks(sellers || []);
        },
        () => {
          this.rows = [];
          this.priceRange.emit(null);
          this.loading = false;
        }
      );
  }

  // ---- staged loading ---------------------------------------------------------
  private cancelLiveChecks(): void {
    this.liveGeneration++;
    this.liveSubs.forEach((s) => s.unsubscribe());
    this.liveSubs = [];
    this.liveState = {};
    this.lastSeenAt = {};
  }

  private startLiveChecks(sellers: any[]): void {
    const pending = sellers.filter((s: any) => s && s.needsLiveCheck && s.site);
    if (!pending.length) return;
    const gen = this.liveGeneration;
    const reg = this.registration.replace(/ /g, '');
    let outstanding = pending.length;
    pending.forEach((s: any) => {
      this.liveState[s.site] = 'checking';
      if (s.last_confirmed) this.lastSeenAt[s.site] = s.last_confirmed;
    });
    pending.forEach((s: any) => {
      const sub = this.registrationService
        .getDealerLivePrice(reg, s.site)
        .pipe(
          timeout(32000),
          catchError(() => of(null))
        )
        .subscribe((res: any) => {
          if (gen !== this.liveGeneration) return; // plate changed meanwhile
          this.applyLiveResult(s.site, res);
          outstanding--;
          if (outstanding === 0) {
            // Re-sort once at the end so rows don't jump while the user reads.
            this.applySort();
          }
          this.emitRange();
        });
      this.liveSubs.push(sub);
    });
  }

  private applyLiveResult(site: string, res: any): void {
    const idx = this.rows.findIndex((r: any) => r.site === site);
    // gone = the dealer says it no longer has the plate; not_listed = no row for this
    // dealer any more. Drop the row: an old price must never pass for a live one.
    if (res && (res.status === 'gone' || res.status === 'not_listed')) {
      if (idx > -1) this.rows = this.rows.filter((r: any) => r.site !== site);
      delete this.liveState[site];
      delete this.lastSeenAt[site];
      return;
    }
    const fresh = res && (res.status === 'refreshed' || res.status === 'fresh') && res.seller;
    if (!fresh) {
      // Unconfirmed (blocked, failed or timed out): keep the stored price, labelled with
      // when the dealer last really showed it ("Last seen 13 Jul"), or as not recently
      // confirmed when we have no date.
      if (res && res.lastConfirmed) this.lastSeenAt[site] = res.lastConfirmed;
      else delete this.lastSeenAt[site];
      this.liveState[site] = 'last-seen';
      return;
    }
    const calc = (this.compareService.calculateFoundRows([res.seller], false, false) || [])[0];
    if (calc && calc.calculated && calc.total > 0) {
      if (idx > -1) this.rows[idx] = calc;
      else this.rows = [...this.rows, calc];
      this.liveState[site] = 'live';
    } else {
      // Live check says this dealer no longer has it.
      if (idx > -1) this.rows = this.rows.filter((r: any) => r.site !== site);
      delete this.liveState[site];
    }
  }

  public get checkingCount(): number {
    return Object.keys(this.liveState).filter((k) => this.liveState[k] === 'checking').length;
  }

  public lastSeenTitle(site: string): string {
    return this.lastSeenAt[site]
      ? "We couldn't confirm this price with the dealer just now"
      : "The dealer hasn't confirmed this price recently";
  }

  public lastSeenLabel(site: string): string {
    const iso = this.lastSeenAt[site];
    if (!iso) return 'Not recently confirmed';
    const d = new Date(iso);
    if (isNaN(d.getTime())) return 'Not recently confirmed';
    return 'Last seen ' + d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  }

  // Lowest/highest live total across all loaded listings (unfiltered — the hero
  // range reflects the whole market for this registration).
  private emitRange(): void {
    if (!this.rows.length) {
      this.priceRange.emit(null);
      return;
    }
    // The hero range uses confirmed prices when there are any; unconfirmed ones
    // (labelled "Last seen …" in the table) only when nothing else is known.
    const confirmed = this.rows.filter((r: any) => this.liveState[r.site] !== 'last-seen');
    const basis = confirmed.length ? confirmed : this.rows;
    const totals = basis.map((r) => r.total);
    this.priceRange.emit({
      low: Math.min(...totals),
      high: Math.max(...totals),
      sellers: this.rows.length,
    });
  }

  public get totalCount(): number {
    return this.rows.length + (this.dvla ? 1 : 0);
  }

  public get title(): string {
    if (this.dvla && !this.rows.length) return 'Buy new from DVLA';
    // No "Compare all 0 sellers" while the listings are still loading.
    if (!this.totalCount) return 'Compare sellers';
    return `Compare all ${this.totalCount} sellers`;
  }

  // In-memory filtering — replicates the EXACT predicates the existing
  // tp-sellers comparison uses (CreateLists). No new backend calls; the rows
  // are already loaded. Display/data untouched — only which rows are shown.
  public get filteredRows(): CompareResult[] {
    return this.rows.filter((r) => {
      if (this.filters.standardPlates && !(r.physicalPlatePrice > -1))
        return false;
      if (this.filters.managedTransfer && !(r.transferService > -1))
        return false;
      if (
        this.filters.offersKlarna &&
        !(r.financeMethods != null && r.financeMethods.indexOf('klarna') > -1)
      )
        return false;
      if (this.filters.offersBespokeFinance && !this.hasBespokeFinance(r))
        return false;
      if (this.filters.rating !== 'any') {
        const rounded = Math.round((r.trustPilotScore || 0) * 2) / 2;
        if (!(rounded >= Number(this.filters.rating))) return false;
      }
      return true;
    });
  }

  // Bespoke finance predicate, mirrored verbatim from tp-sellers.CreateLists.
  private hasBespokeFinance(r: CompareResult): boolean {
    if (r.finance) return true;
    if (r.financeMethods != null && r.financeMethods.indexOf('klarna') == -1)
      return true;
    return false;
  }

  public get filteredCount(): number {
    return this.filteredRows.length;
  }

  public get visibleRows(): CompareResult[] {
    const rows = this.filteredRows;
    return this.expanded ? rows : rows.slice(0, this.pageSize);
  }

  public get remaining(): number {
    return Math.max(0, this.filteredCount - this.visibleRows.length);
  }

  public get activeFilterCount(): number {
    let n = 0;
    if (this.filters.standardPlates) n++;
    if (this.filters.managedTransfer) n++;
    if (this.filters.offersKlarna) n++;
    if (this.filters.offersBespokeFinance) n++;
    if (this.filters.rating !== 'any') n++;
    return n;
  }

  public toggleFilters(): void {
    this.filtersOpen = !this.filtersOpen;
  }

  public clearFilters(): void {
    this.filters = {
      standardPlates: false,
      managedTransfer: false,
      offersKlarna: false,
      offersBespokeFinance: false,
      rating: 'any',
    };
  }

  public setSort(value: string): void {
    this.sort = value as SortKey;
    this.applySort();
  }

  private applySort(): void {
    this.rows = [...this.rows].sort((a, b) => {
      if (this.sort === 'price-desc') return b.total - a.total;
      if (this.sort === 'trust') {
        return (b.trustPilotScore || -1) - (a.trustPilotScore || -1);
      }
      return a.total - b.total; // price low to high (default)
    });
  }

  public showMore(): void {
    this.expanded = true;
  }

  public get supportingCopy(): string {
    if (this.dvla && !this.rows.length)
      return 'DVLA’s own price for this new release plate. Dealer listings appear here as sellers add it.';
    return this.kind === 'new-issue'
      ? 'Live prices from the new issue source and verified sellers. Compare each on price, finance, transfer and reviews.'
      : 'Live prices from verified sellers. Compare each on price, finance, transfer and reviews.';
  }

  // ---- per-seller attribute helpers (mirror existing tp-sellers semantics) ----
  // transferService / physicalPlatePrice are pence: -1 = unavailable, 0 = FREE.
  public hasFee(value: number): boolean {
    return value != null && value >= 0;
  }

  public feeLabel(value: number): string {
    if (!this.hasFee(value)) return '';
    return value === 0 ? 'FREE' : '£' + (value / 100).toFixed(2);
  }

  // Existing Trustpilot rendering — CDN star image keyed on the rounded score.
  public trustImage(r: CompareResult): string {
    const rating = Math.round((r.trustPilotScore || 0) * 2) / 2;
    return `https://cdn.trustpilot.net/brand-assets/4.1.0/stars/stars-${rating}.svg`;
  }

  public hasTrust(r: CompareResult): boolean {
    return r.trustPilotScore != null && r.trustPilotRatingCount > 0;
  }

  public openTrustpilot(r: CompareResult): void {
    if (r.trustPilotLink) window.open(r.trustPilotLink, '_blank');
  }

  // Existing affiliate-click behaviour, preserved verbatim.
  public visit(r: CompareResult): void {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: `Affiliate - ${this.registration.replace(' ', '')} (${
          r.viewName
        })`,
      });
    }

    this.trackingService.TrackAffiliateDetail(this.registration, r.viewName, {
      dealer: r,
    });

    if (r.sourceId) {
      this.commerceService
        .recordClick({
          registration: this.registration,
          sourceId: r.sourceId,
          clickKind: 'OUTBOUND',
          destinationUrl: r.url,
        })
        .subscribe(
          () => {},
          () => {}
        );
    }

    const sep = r.url.indexOf('?') === -1 ? '?' : '&';
    window.open(`${r.url}${sep}token=${this.sessionService.getToken()}`, '_blank');
  }
}
